import { useEffect, useState } from "react";
import {
  api,
  type GameCycle,
  type CampaignTimingSettings,
} from "./api";
import { SituationSection } from "./cycle/SituationSection";
import { ChronicleSection } from "./cycle/ChronicleSection";
import { ActionSection } from "./cycle/ActionSection";
import { HqSection } from "./cycle/HqSection";
import {
  isKnownCycleSection,
  DeadlineBadge,
  dayProgress,
  phaseInfo,
  type Props,
  type Section,
  type CycleSection,
} from "./cycle/shared";
export type { CycleSection } from "./cycle/shared";

export function CycleTab(props: Props) {
  const cycle = props.setup.cycle.current;
  if (!cycle) return <CycleStart {...props} />;
  if (cycle.phase === "PREPARING")
    return (
      <CycleProgress
        title="Создаём ситуацию нового цикла"
        text="Формируется проблема, личные сведения и иллюстрации. Экран обновится автоматически."
        value={24}
      />
    );
  return <ActiveCycle {...props} />;
}

function CycleStart({ campaignId, setup, action }: Props) {
  return (
    <section className="cc-block cc-cycle-launch">
      <div className="eyebrow">Полный игровой цикл</div>
      <h2>От ситуации до последствий</h2>
      <p>
        Игроки получат проблему, выполнят действия, проведут переговоры,
        подготовят инициативы, проголосуют и увидят последствия.
      </p>
      {setup.canManage && (
        <TimingSettingsForm
          campaignId={campaignId}
          settings={setup.cycle.timingSettings}
          action={action}
        />
      )}
      {setup.cycle.canStartCycle ? (
        <button
          className="primary cc-main-action"
          type="button"
          onClick={() =>
            void action("Создаём новый цикл…", () => api.startCycle(campaignId))
          }
        >
          Начать цикл
        </button>
      ) : (
        <p className="cc-waiting-note">Новый цикл открывает организатор.</p>
      )}
    </section>
  );
}

function ActiveCycle(props: Props) {
  const cycle = props.setup.cycle.current!;
  const tabs = cycleNavigationFor(cycle);
  const [internalSection, setInternalSection] = useState<CycleSection>(
    tabs[0].id,
  );
  const requestedSection = props.activeSection ?? internalSection;
  const active = isKnownCycleSection(requestedSection) && tabs.some((tab) => tab.id === requestedSection)
    ? requestedSection
    : tabs[0].id;
  const navigate = (next: CycleSection) => {
    setInternalSection(next);
    props.onSectionChange?.(next);
  };

  const hubMode = active === "situation";

  return (
    <div className={`cc-tab-stack cc-cycle-root${hubMode ? " hub-mode" : ""}`}>
      {!hubMode && active !== "actions" && <CycleHeader cycle={cycle} />}
      <main className="cc-cycle-section">
        {renderSection(active, props, navigate)}
      </main>
    </div>
  );
}

function renderSection(
  section: Section,
  props: Props,
  setSection: (section: Section) => void,
) {
  const cycle = props.setup.cycle.current!;
  if (cycle.phase === "RESOLVING")
    return (
      <CycleProgress
        title="Рассчитываем последствия"
        text="Движок применяет решения к ресурсам, институтам, фракциям и показателям."
        value={82}
      />
    );
  if (cycle.phase === "NARRATING")
    return (
      <CycleProgress
        title="Создаём итоговую хронику"
        text="Механические изменения уже рассчитаны. Формируется только повествование."
        value={94}
      />
    );

  switch (section) {
    case "situation":
      return <SituationSection {...props} onNavigate={setSection} />;
    case "actions":
      return <ActionSection {...props} onNavigate={setSection} />;
    case "chronicle":
      return <ChronicleSection {...props} />;
    case "hq":
      return <HqSection {...props} onNavigate={setSection} />;
  }
}

function CycleHeader({ cycle }: { cycle: GameCycle }) {
  const phase = phaseInfo(cycle.phase);
  const progress = dayProgress(cycle);
  return (
    <section className="cc-block cc-cycle-status cc-cycle-status-compact">
      <div className="cc-cycle-status-main">
        <span>Цикл {cycle.cycleNumber}</span>
        <h2>{cycle.title}</h2>
        <p>{phase.label}</p>
      </div>
      <div className="cc-cycle-deadline-row">
        <DeadlineBadge cycle={cycle} />
      </div>
      <div
        className="cc-day-progress-mini"
        aria-label={`День завершён на ${progress.percent}%`}
      >
        <span>Прогресс дня</span>
        <div>
          <i style={{ width: `${progress.percent}%` }} />
        </div>
        <b>{progress.percent}%</b>
      </div>
    </section>
  );
}

function TimingSettingsForm({
  campaignId,
  settings,
  action,
}: {
  campaignId: string;
  settings: CampaignTimingSettings;
  action: Props["action"];
}) {
  const [draft, setDraft] = useState(settings);
  useEffect(
    () => setDraft(settings),
    [
      settings.dayDurationMinutes,
      settings.closingDurationMinutes,
      settings.votingDurationMinutes,
      settings.npcVisitDurationMinutes,
    ],
  );
  const field = (key: keyof CampaignTimingSettings, value: number) =>
    setDraft((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="cc-timing-settings"
      onSubmit={(event) => {
        event.preventDefault();
        void action("Сохраняем сроки цикла…", () =>
          api.updateCycleTimingSettings(campaignId, draft),
        );
      }}
    >
      <div className="cc-section-mini">
        <strong>Сроки асинхронного цикла</strong>
        <span>Фазы завершаются раньше, если все готовы.</span>
      </div>
      <div className="cc-timing-grid">
        <label>
          День, часов
          <input
            type="number"
            min={1}
            max={168}
            value={Math.max(1, Math.round(draft.dayDurationMinutes / 60))}
            onChange={(event) =>
              field(
                "dayDurationMinutes",
                Math.max(30, Number(event.target.value) * 60),
              )
            }
          />
        </label>
        <label>
          Последние встречи, минут
          <input
            type="number"
            min={10}
            max={720}
            value={draft.closingDurationMinutes}
            onChange={(event) =>
              field("closingDurationMinutes", Number(event.target.value))
            }
          />
        </label>
        <label>
          Голосование, часов
          <input
            type="number"
            min={1}
            max={48}
            value={Math.max(1, Math.round(draft.votingDurationMinutes / 60))}
            onChange={(event) =>
              field(
                "votingDurationMinutes",
                Math.max(15, Number(event.target.value) * 60),
              )
            }
          />
        </label>
        <label>
          Одна встреча, минут
          <input
            type="number"
            min={10}
            max={720}
            value={draft.npcVisitDurationMinutes}
            onChange={(event) =>
              field("npcVisitDurationMinutes", Number(event.target.value))
            }
          />
        </label>
      </div>
      <button type="submit">Сохранить сроки</button>
    </form>
  );
}

function CycleProgress({
  title,
  text,
  value,
}: {
  title: string;
  text: string;
  value: number;
}) {
  return (
    <section className="cc-block cc-cycle-progress">
      <span className="cc-spinner" />
      <div>
        <div className="eyebrow">Обработка продолжается</div>
        <h2>{title}</h2>
        <p>{text}</p>
        <div className="cc-progress">
          <i style={{ width: `${value}%` }} />
        </div>
      </div>
      <strong>{value}%</strong>
    </section>
  );
}

export function cycleNavigationFor(cycle?: GameCycle): Array<{
  id: CycleSection;
  label: string;
  icon: string;
  badge?: string;
  unread?: boolean;
}> {
  if (!cycle) return [{ id: "situation", label: "Мир", icon: "◉" }];
  const actionBadge = cycle.phase === "DAY"
    ? `${cycle.actionsUsed}/${cycle.actionLimit}`
    : cycle.phase === "AGENDA" || cycle.phase === "VOTING"
      ? `${cycle.initiatives.filter((item) => item.status !== "MERGED" && item.consolidatedIntoId == null).length}`
      : cycle.actions.length ? `${cycle.actions.length}` : undefined;
  return [
    {
      id: "situation",
      label: "Мир",
      icon: "◉",
    },
    {
      id: "actions",
      label: "Действия",
      icon: "◆",
      badge: actionBadge,
      unread: cycle.unread.initiativeCount > 0 || cycle.unread.chatCount > 0,
    },
    {
      id: "chronicle",
      label: "Хроника",
      icon: "▤",
      badge: cycle.unread.chronicleCount > 0 ? `${cycle.unread.chronicleCount}` : undefined,
      unread: cycle.unread.chronicleCount > 0,
    },
    {
      id: "hq",
      label: "Штаб",
      icon: "☷",
    },
  ];
}

